import React, { useMemo } from 'react';

import { EmbedSource } from 'src/types/__generated__/graphql';

interface Props {
    embedId: string;
    embedSource: EmbedSource;
    width?: string | number;
    height?: string | number;
    autoplay?: boolean;
    allowFullScreen?: boolean;
    showOrigin?: boolean;
}

type EmbedYoutubeVideoProps = Omit<Props, 'embedSource'>;
const EmbedYoutubeVideo: React.FC<EmbedYoutubeVideoProps> = ({
    embedId,
    width,
    height,
    autoplay,
    allowFullScreen,
    showOrigin = true,
}) => {
    const src = useMemo(() => {
        const url = `https://www.youtube.com/embed/${embedId}`;

        const params = [];
        if (autoplay) {
            // need to add "mute=1" to autoplay, modern browsers like Chrome doesn't allow autoplay with sound (because it's considered annoying)
            params.push('autoplay=1&mute=1');
        }
        if (showOrigin) {
            params.push(`origin=${process.env.NEXT_PUBLIC_SITE_URL}`);
        }

        return params.length ? `${url}?${params.join('&')}` : url;
    }, [autoplay, showOrigin, embedId]);

    const allow = useMemo(() => {
        const permissions = ['accelerometer', 'encrypted-media', 'gyroscope', 'picture-in-picture', 'web-share'];
        if (autoplay) {
            permissions.push('autoplay');
        }
        return permissions.length ? permissions.join('; ') : undefined;
    }, [autoplay]);

    return (
        <iframe
            width={width}
            height={height}
            title="Youtube Video"
            src={src}
            allow={allow}
            style={{ border: 0 }}
            allowFullScreen={allowFullScreen}
        />
    );
};

const EmbedVideo: React.FC<Props> = (props) => {
    const { embedSource, ...rest } = props;

    switch (embedSource) {
        case EmbedSource.Youtube:
            return <EmbedYoutubeVideo {...rest} />;
        default:
            return null;
    }
};

export default EmbedVideo;
